import apiClient from './apiClient'
import { _OperationDetailsRequest } from '../features/cafm/operationDetailsSlice'
import { SecretUpdatePayload } from '../features/cafm/secretValueSlice'
import { NewAddedValueMapping } from '../components/EditFieldMapping/ValueMapping'
import { updateAPIBaseURLtype } from '../components/Common/OrganizationDetailsComponent'
import { omit } from 'lodash'

// MIME type constants
const MIME_TYPES = {
  APPLICATION_JSON: 'application/json',
} as const

interface ValueMapping {
  value_mapping_id?: string
  source_field_name: string
  converted_value?: string
  source_value?: string
  destination_field_name?: string | null
}

type FieldMapping = {
  field_name: string
  is_mandatory: boolean
  data_type: 'string' | 'number' | 'boolean' | 'object' | 'array'
  request_location: string
  default_value: string | number | boolean | null
  source_object_field_name?: string
  new_field_id?: string
  match_values?: {
    match_value_type: string
    values_mapping: ValueMapping[]
  }
}

type UpdateFieldMapping = {
  field_id: string
  field_name: string
  is_mandatory: boolean
  data_type: null | 'body' | 'query' | 'params'
  request_location: string
  default_value: string | number | boolean | null
  source_object_field_name?: string
}

export type RequestPayload = {
  operation_id: string
  api_endpoint_url?: string
  api_method?: string
  api_header_content_type?: string
  decider_fields_name?: string
  decider_fields_value: string[]
  added_new_field_mapping?: FieldMapping[]
  update_field_mapping?: UpdateFieldMapping[]
  delete_field_mapping?: string[]
}

// Create a new CAFM mapping
export const createCafmMapping = async (data: any) => {
  const response = await apiClient.post('/cafm', data)
  return response
}

interface FetchCafmDetailsParams {
  start: number
  limit: number
  org_id?: string // Optional for admin override, otherwise uses current org
  isPersonalOrg?: boolean // Flag for dummy Personal org (id=0)
  external_id?: string // External ID for Personal orgs
}

export const fetchCafmDetails = async ({
  start,
  limit,
  org_id,
  isPersonalOrg,
  external_id,
}: FetchCafmDetailsParams) => {
  console.log('fetchCafmDetails called with:', { start, limit, org_id, isPersonalOrg, external_id })
  const payload: any = { start, limit }

  if (isPersonalOrg) {
    // For Personal orgs, send external_id instead of org_id
    if (external_id) {
      payload.external_id = external_id
      console.log('Using provided external_id:', external_id)
    } else {
      // Fallback: try sessionStorage
      console.log('No external_id provided, checking sessionStorage...')
      const orgs = JSON.parse(sessionStorage.getItem('organizations') || '[]')
      const personalOrg = orgs.find((org: any) => org.type?.toLowerCase() === 'personal')
      if (personalOrg?.externalId) {
        payload.external_id = personalOrg.externalId
        console.log('Found external_id in sessionStorage:', personalOrg.externalId)
      } else {
        console.error('Personal org external_id not found in parameter or sessionStorage')
        throw new Error('Personal organization external_id not found - please provide external_id parameter')
      }
    }
  } else if (org_id) {
    payload.org_id = org_id
    console.log('Using org_id:', org_id)
  }

  console.log('Final payload for /cafm/details:', payload)
  const response = await apiClient.post('/cafm/details', payload)
  console.log('API response received:', response)
  return response
}

// Fetch a single CAFM mapping by ID
export const fetchCafmById = async (id: any) => {
  const response = await apiClient.get(`/cafm/${id}`)
  return response
}

// Fetch a single CAFM mapping by ID
const source_systems = ['fsm', 'Konect', 'Konect ENA']
// Fetch a single CAFM mapping by ID
export const fetchMasterData = async (source_system: string) => {
  console.log('cafmApi - fetchMasterData called with source_system:', source_system)
  // Normalize incoming value
  const normalized = source_system.toLowerCase()
  // Try to find the correct one from the array
  const matchedSource = source_systems.find((sys) => sys.toLowerCase() === normalized)
  if (!matchedSource) {
    console.warn(
      `cafmApi - Invalid source_system "${source_system}" passed. Allowed values: ${source_systems.join(', ')}`,
    )
    throw new Error(`Invalid source_system: ${source_system}`)
  }
  console.log('cafmApi - payload being sent:', { source_system: matchedSource })
  const response = await apiClient.post(`/cafm/master-data`, { source_system: matchedSource })
  return response
}

// delete single CAFM mapping details
export const deleteMapping = async (payload: { integration_id: string; org_id: string }) => {
  const response = await apiClient.delete('/cafm/cafm-endpoint', { data: payload })
  return response
}

// Get integration details - supports normal, multi, and encrypted modes
export const getIntegrationDetails = async (payload: { integration_id: number | number[]; release?: boolean }) => {
  const response = await apiClient.post('/cafm/get-integration-details', payload)
  return response
}

// Decrypt integration data using Web Crypto API
export const decryptIntegrationData = async (encryptedFile: {
  data: string
  encryption: { iv: string }
  metadata: { kone_customer: string; kone_system: string }
}) => {
  try {
    const { data: encryptedData, encryption, metadata } = encryptedFile
    const { iv } = encryption
    const { kone_customer: customerName, kone_system: sourceSystem } = metadata

    // Create the same deterministic key used for encryption
    const keyString = `${customerName}_${sourceSystem}_kone_integration`
    const keyBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(keyString))

    // Import the key for decryption
    const cryptoKey = await crypto.subtle.importKey('raw', keyBuffer, { name: 'AES-CBC' }, false, ['decrypt'])

    // Convert hex strings to ArrayBuffers
    const encryptedBuffer = new Uint8Array(encryptedData.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || [])
    const ivBuffer = new Uint8Array(iv.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || [])

    // Decrypt the data
    const decryptedBuffer = await crypto.subtle.decrypt({ name: 'AES-CBC', iv: ivBuffer }, cryptoKey, encryptedBuffer)

    // Convert back to JSON
    const decryptedText = new TextDecoder().decode(decryptedBuffer)
    return JSON.parse(decryptedText)
  } catch (error) {
    console.error('Decryption failed:', error)
    throw new Error('Failed to decrypt integration data: ' + (error as Error).message)
  }
}

// Fetch CAFM details with pagination
export const workorderApi = async ({ start, limit }: { start: number; limit: number }) => {
  const response = await apiClient.post('/cafm/workorder', { start, limit })
  return response
}

// cafmAPI.ts -- dummy api
export const updateApiBaseUrl = async (apiUrl: string) => {
  try {
    const response = await fetch('/api/organization-details', {
      method: 'POST',
      headers: {
        'Content-Type': MIME_TYPES.APPLICATION_JSON,
      },
      body: JSON.stringify({ apiUrl }),
    })
    if (!response.ok) {
      throw new Error('Failed to update API URL')
    }
    return await response.json()
  } catch (error) {
    console.error('Error updating API Base URL:', error)
    throw error
  }
}

export const getFieldsMappingDetailsViaOperationId = async (operation_id: string) => {
  const response = await apiClient.post('/cafm/field-mapping/details', { operation_id })
  return response
}

/**
 * Utility function to remove value_mapping_id from field mapping payload
 * This ensures the field mapping API doesn't receive value_mapping_id
 */
const removeValueMappingIds = (payload: RequestPayload): RequestPayload => {
  const cleanPayload = { ...payload }

  // Clean added_new_field_mapping
  if (cleanPayload.added_new_field_mapping) {
    cleanPayload.added_new_field_mapping = cleanPayload.added_new_field_mapping.map((field) => {
      const cleanField = { ...field }

      // Remove value_mapping_id from match_values.values_mapping if it exists
      if (cleanField.match_values?.values_mapping) {
        cleanField.match_values = {
          ...cleanField.match_values,
          values_mapping: cleanField.match_values.values_mapping.map((mapping) => omit(mapping, 'value_mapping_id')),
        }
      }

      return cleanField
    })
  }

  // Clean update_field_mapping (if it has similar structure in the future)
  if (cleanPayload.update_field_mapping) {
    cleanPayload.update_field_mapping = cleanPayload.update_field_mapping.map((field) => {
      const cleanField = { ...field } as any

      // Remove value_mapping_id from match_values.values_mapping if it exists
      if (cleanField.match_values?.values_mapping) {
        cleanField.match_values = {
          ...cleanField.match_values,
          values_mapping: cleanField.match_values.values_mapping.map((mapping: any) =>
            omit(mapping, 'value_mapping_id'),
          ),
        }
      }

      return cleanField
    })
  }

  return cleanPayload
}

export const updateFieldMappingDetails = async (request: RequestPayload) => {
  // Remove value_mapping_id from the payload before sending to field mapping API
  const cleanedRequest = removeValueMappingIds(request)
  const response = await apiClient.patch('/cafm/field-mapping', cleanedRequest)
  return response
}

// store CAFM endpoints
export const storeCafmEndpoint = async (payload: object) => {
  const response = await apiClient.post(`/cafm/endpoint`, payload)
  return response
}

//get CAFM endpoints
export const getCafmEndpoints = async (integrationId: string, start: number, limit: number) => {
  const response = await apiClient.post(`/cafm/endpoints`, { int_id: integrationId, start, limit })
  return response
}

export const getOperationDetails = async (payload: _OperationDetailsRequest) => {
  const response = await apiClient.post(`cafm/mappings`, payload)
  return response
}

export const getSecretValue = async (integation_id: string) => {
  const response = await apiClient.get(`cafm/secret/${integation_id}`)
  return response
}

export const updateSecretValue = async (payload: SecretUpdatePayload) => {
  const response = await apiClient.patch(`cafm/secret`, payload)
  return response
}

export const updateValueMapping = async (payload: NewAddedValueMapping) => {
  const response = await apiClient.patch(`cafm/value-mapping`, payload)
  return response
}

export const updateAPIBaseURL = async (payload: updateAPIBaseURLtype) => {
  const response = await apiClient.patch(`cafm/api-base-url`, payload)
  return response
}

// delete single CAFM mapping endpoint
export const deleteEndpoint = async (payload: { operation_id: number }) => {
  const response = await apiClient.delete('/cafm/endpoint-details', { data: payload })
  return response
}

// Dummy API: Check if current org has existing mapping
export const checkOrgHasMapping = async (orgId: string) => {
  // TODO: Replace with real API call
  // Simulate: returns { hasMapping: boolean }
  console.log('Checking mapping for org:', orgId)
  return Promise.resolve({ hasMapping: false })
}

// Dummy API: Get list of organizations to replicate from
export const fetchReplicableOrganizations = async () => {
  // TODO: Replace with real API call
  // Simulate: returns [{ id: string, name: string }]
  return Promise.resolve([
    { id: 'org1', name: 'Organization One' },
    { id: 'org2', name: 'Organization Two' },
    { id: 'org3', name: 'Organization Three' },
  ])
}

// API to replicate integration configuration
export const copyIntegration = async (integration_id: string, target_org: string) => {
  const response = await apiClient.post('/cafm/copy-integration', {
    integration_id: Number(integration_id),
    target_org: Number(target_org),
  })
  return response
}

// Release Config API interface
interface ReleaseConfigData {
  release: boolean
  integration_id: string
  customer_name: string
  base_url: string
  auth_type: 'basic' | 'custom' | 'header'
  // Basic auth fields
  auth_url?: string
  username_body?: string
  password_body?: string
  // Custom auth fields (includes basic + additional)
  username_auth?: string
  password_auth?: string
  // Header auth fields
  headerKey?: string
  headerValue?: string
}

// API to export release configuration
export const exportReleaseConfig = async (data: Omit<ReleaseConfigData, 'release'>) => {
  const payload: ReleaseConfigData = {
    ...data,
    release: true,
  }

  try {
    // Request the file data from the backend (now returns JSON with filename and hex-encoded file)
    const response = await apiClient.post('/cafm/get-integration-details', payload)

    console.log('Release config response:', response)

    // Check if response has the expected structure
    // The data might be at response.data level or response level depending on API client setup
    const responseData = response.data || response
    if (!responseData || !responseData.filename || !responseData.file) {
      throw new Error('Invalid response format: missing filename or file data')
    }

    const { filename, file: hexString } = responseData

    console.log('Filename from response:', filename)
    console.log('Hex string length:', hexString.length)

    // Create downloadable blob with the raw hex string (no decryption/conversion)
    // The file should contain the hex string exactly as received from backend
    const blob = new Blob([hexString], { type: MIME_TYPES.APPLICATION_JSON })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename // Use exact filename from response
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }, 100)

    console.log('Download triggered with filename:', filename)
    return response
  } catch (error) {
    console.error('Release config export failed:', error)
    throw error
  }
}

// API to import release configuration
export const importReleaseConfig = async (fileContent: string, filename: string, forceUpdate: boolean) => {
  try {
    const payload = {
      filename: filename || 'integration_export.json',
      file: fileContent,
      force_update: forceUpdate,
    }

    console.log('Import payload:', payload)

    const response = await apiClient.post('/cafm/import-integration', payload)
    return response
  } catch (error) {
    console.error('Release config import failed:', error)
    throw error
  }
}

// API to send integration configuration via email
interface SendIntegrationEmailRequest {
  integration_id: string
  customer_name: string
  base_url: string
  auth_type: 'basic' | 'custom' | 'header' | 'apiKey'
  user_email?: string // User email to be added to CC
  // Basic auth fields
  auth_url?: string
  username_body?: string
  password_body?: string
  // Custom auth fields (includes basic + additional)
  username_auth?: string
  password_auth?: string
  // Header auth fields
  headerKey?: string
  headerValue?: string
  // API Key auth fields
  token?: string
}

export interface SendIntegrationEmailResponse {
  email_sent: boolean
  filename: string
  messageId: string
  recipients: string[]
}

export const sendIntegrationEmail = async (
  data: SendIntegrationEmailRequest,
): Promise<SendIntegrationEmailResponse> => {
  try {
    const response = await apiClient.post('/cafm/send-integration-email', data)
    return response.data
  } catch (error) {
    console.error('Send integration email failed:', error)
    throw error
  }
}

interface ExecuteTestData {
  organization_id: string
  organization_name: string
  source_system: string
  ken: string
  customer_po_number: string
  unique_id: string
}

// API to sent data for execute tests for onboarding tool
export const runTests = async (runTestData: ExecuteTestData) => {
  try {
    const payload = {
      organization_id: runTestData.organization_id,
      organization_name: runTestData.organization_name,
      source_system: runTestData.source_system,
      ken_number: runTestData.ken,
      customer_po_number: runTestData.customer_po_number,
      unique_id: runTestData.unique_id,
    }

    console.log('Import payload:', payload)

    const response = await apiClient.post('/cafm/execute-onboarding-tests', payload)
    return response
  } catch (error) {
    console.error('Run tests failed:', error)
    throw error
  }
}
